import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  createChatConversation,
  joinChatConversation,
} from "../../api/ProcurementClient";
import { ChatConversation } from "../../models/Chat";
import User from "../../models/User";
import ChatAvatar, { ConversationAvatar } from "./ChatAvatar";
import { userDisplayName } from "./chatUtils";

interface PeoplePickerProps {
  options: User[];
  value: User[];
  onChange: (users: User[]) => void;
  label: string;
  autoFocus?: boolean;
}

const PeoplePicker: React.FC<PeoplePickerProps> = ({ options, value, onChange, label, autoFocus }) => (
  <Autocomplete
    multiple
    options={options}
    value={value}
    onChange={(_, next) => onChange(next)}
    getOptionLabel={userDisplayName}
    isOptionEqualToValue={(a, b) => a.id === b.id}
    filterSelectedOptions
    renderOption={(props, option) => {
      return (
        <li {...props} key={option.id}>
          <Stack direction="row" alignItems="center" gap={1}>
            <ChatAvatar user={option} size={24} />
            <Box>
              <Typography variant="body2">{userDisplayName(option)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            </Box>
          </Stack>
        </li>
      );
    }}
    renderInput={(params) => <TextField {...params} label={label} autoFocus={autoFocus} />}
  />
);

interface NewChatDialogProps {
  open: boolean;
  onClose: () => void;
  staff: User[];
  meId: number;
  onCreated: (conversation: ChatConversation) => void;
}

export const NewChatDialog: React.FC<NewChatDialogProps> = ({ open, onClose, staff, meId, onCreated }) => {
  const [people, setPeople] = useState<User[]>([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPeople([]);
      setName("");
      setError(null);
    }
  }, [open]);

  const options = useMemo(() => staff.filter((u) => u.id !== meId), [staff, meId]);
  const isGroup = people.length > 1;

  const create = async () => {
    if (people.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const conversation = isGroup
        ? await createChatConversation({
            type: "group",
            memberIds: people.map((u) => u.id),
            name: name.trim() || undefined,
          })
        : await createChatConversation({ type: "direct", userId: people[0].id });
      onCreated(conversation);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start chat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New chat</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <PeoplePicker
            options={options}
            value={people}
            onChange={setPeople}
            label="To: enter names"
            autoFocus
          />
          {isGroup && (
            <TextField
              label="Group name (optional)"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void create()} disabled={saving || people.length === 0}>
          {isGroup ? "Create group" : "Start chat"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface NewChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (conversation: ChatConversation) => void;
}

export const NewChannelDialog: React.FC<NewChannelDialogProps> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setError(null);
    }
  }, [open]);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const conversation = await createChatConversation({
        type: "channel",
        name: name.trim(),
        description: description.trim(),
      });
      onCreated(conversation);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create channel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create a channel</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Channels are open to all staff. Anyone can browse and join.
          </Typography>
          <TextField
            label="Channel name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
            inputProps={{ maxLength: 256 }}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void create()} disabled={saving || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface BrowseChannelsDialogProps {
  open: boolean;
  onClose: () => void;
  channels: ChatConversation[];
  meId: number;
  usersById: Map<number, User>;
  onOpen: (conversation: ChatConversation) => void;
  onJoined: (conversation: ChatConversation) => void;
}

export const BrowseChannelsDialog: React.FC<BrowseChannelsDialogProps> = ({
  open,
  onClose,
  channels,
  meId,
  usersById,
  onOpen,
  onJoined,
}) => {
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setFilter("");
      setError(null);
    }
  }, [open]);

  const visible = channels
    .filter((c) => !c.archivedAt)
    .filter((c) => c.name.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const join = async (channel: ChatConversation) => {
    setBusyId(channel.id);
    setError(null);
    try {
      onJoined(await joinChatConversation(channel.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to join channel");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Browse channels</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search channels"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          autoFocus
          sx={{ mb: 1 }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {visible.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No channels yet. Create one to get started.
          </Typography>
        ) : (
          <List dense disablePadding>
            {visible.map((channel) => (
              <ListItem
                key={channel.id}
                disableGutters
                secondaryAction={
                  channel.joined ? (
                    <Button size="small" onClick={() => onOpen(channel)}>
                      Open
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busyId === channel.id}
                      onClick={() => void join(channel)}
                    >
                      Join
                    </Button>
                  )
                }
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{ cursor: "pointer", pr: 10, minWidth: 0 }}
                  onClick={() => onOpen(channel)}
                >
                  <ConversationAvatar conversation={channel} meId={meId} usersById={usersById} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {channel.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {channel.memberIds.length} {channel.memberIds.length === 1 ? "member" : "members"}
                      {channel.description ? ` · ${channel.description}` : ""}
                    </Typography>
                  </Box>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface AddPeopleDialogProps {
  open: boolean;
  onClose: () => void;
  staff: User[];
  excludeIds: number[];
  onAdd: (userIds: number[]) => Promise<void>;
}

export const AddPeopleDialog: React.FC<AddPeopleDialogProps> = ({ open, onClose, staff, excludeIds, onAdd }) => {
  const [people, setPeople] = useState<User[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPeople([]);
      setError(null);
    }
  }, [open]);

  const options = useMemo(() => staff.filter((u) => !excludeIds.includes(u.id)), [staff, excludeIds]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await onAdd(people.map((u) => u.id));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add people");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add people</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <PeoplePicker options={options} value={people} onChange={setPeople} label="People" autoFocus />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void add()} disabled={saving || people.length === 0}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TextPromptDialogProps {
  open: boolean;
  title: string;
  label: string;
  initialValue: string;
  multiline?: boolean;
  required?: boolean;
  onClose: () => void;
  onSave: (value: string) => Promise<void>;
}

export const TextPromptDialog: React.FC<TextPromptDialogProps> = ({
  open,
  title,
  label,
  initialValue,
  multiline,
  required,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <TextField
            label={label}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoFocus
            multiline={multiline}
            minRows={multiline ? 3 : undefined}
            onKeyDown={(event) => {
              if (!multiline && event.key === "Enter") {
                event.preventDefault();
                void save();
              }
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || (required && !value.trim())}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
